import {AppBar, Box} from '@mui/material';
import {Outlet} from 'react-router';
import {TopNav} from './TopNav';
import {useDevice} from '@/hooks/useDevice.ts';
import {BottomNav} from '@/components/layout/BottomNav.tsx';

// See the pb comment inside AppLayout's main Box below for what this
// guards against and why it must track BottomNav.tsx's own Toolbar height.
export const BOTTOM_NAV_RESERVED_HEIGHT = 61;

export function AppLayout() {
   const device = useDevice();
   return (
      <Box sx={{minHeight: '100vh', bgcolor: !device.isPhoneOrTablet ? 'background.default' : 'background.paper'}}>
         {!device.isPhoneOrTablet && (
            <AppBar position='sticky' elevation={0}>
               <TopNav />
            </AppBar>
         )}

         <Box
            component='main'
            sx={{
               // No maxWidth cap (removed 2026-09-01, was 1400 + mx:
               // 'auto' to center it) — Duane's call: MRT tables (10
               // pages' worth — Dashboard/WellsListPage/RunTicketsPage/
               // BarrelTestsPage/ReportsPage/AuditReportPage/
               // Ogp03iExportPage/TanksAllocationsReportPage/
               // UsersAdminPage/WellsAdminPage, all `width: '100%'` on
               // their own table wrapper with no inner cap of their own)
               // were scrolling horizontally on wide desktop screens
               // instead of using the available width. Confirmed safe to
               // do globally rather than per-table-page: the form-heavy
               // pages that would otherwise stretch too wide already cap
               // their OWN inner content independently (WellEditPage.tsx
               // maxWidth: 640/1000, LeaseEditPage.tsx maxWidth: 480-900,
               // etc.) — this Box's own pl/pr padding below is the only
               // "margin" left, same left/right breathing room every
               // page already had.
               // Decomposed from a single `p: {xs: 1, sm: 1, md: 4}` shorthand
               // 2026-08-29 (Duane) — see the pb comment below. MUI/emotion
               // inserts each breakpoint's CSS as its own rule, and the `p`
               // shorthand's rules landed AFTER the plain `pb` rule in the
               // stylesheet, so on mobile the shorthand's `padding: 8px`
               // silently clobbered `padding-bottom: 61px` despite `pb`
               // being written later in this object (object key order does
               // NOT determine CSS cascade order here — confirmed live by
               // reading the actual generated stylesheet rules, which showed
               // the unconditional padding-bottom:61px rule followed by a
               // separate `(min-width: 0px) { padding: 8px }` rule that won
               // the cascade). Splitting into pt/pr/pl removes the overlap
               // entirely so pb can never be shorthand-clobbered again.
               pt: {xs: 1, sm: 1, md: 4},
               pr: {xs: 1, sm: 1, md: 4},
               pl: {xs: 1, sm: 1, md: 4},
               // FIXED 2026-08-29 (Duane): "Run Tickets cut off the last
               // ticket in the list." BottomNav below is `position:
               // fixed, bottom: 0` — genuinely removed from document
               // flow, so nothing here was reserving room for it. Any
               // page whose content reaches the natural bottom of the
               // document on mobile (RunTicketsPage's own hand-rolled
               // card list, not the bounded/scrollable desktop MRT
               // table — see its own comment on why mobile "keeps
               // scrolling the document as before") had its last ~60px
               // rendered directly underneath the bar, invisible and
               // unreachable by scrolling any further. Confirmed live:
               // scrolled a real mobile viewport to the true document
               // bottom and measured the fixed bar's top edge sitting
               // 61px above the viewport's bottom edge, with this Box's
               // own content extending all the way to that same edge —
               // zero reserved gap. BOTTOM_NAV_RESERVED_HEIGHT below
               // matches BottomNav.tsx's own Toolbar `minHeight: 60` +
               // its 1px top border — keep the two in sync if either
               // changes. This is a page-agnostic layout fix (every
               // mobile route renders through this same Box), not
               // specific to Run Tickets — it just happened to be the
               // one page whose content reliably reaches the document's
               // true end without some other bounded/scrollable
               // container already absorbing the difference.
               pb: device.isPhoneOrTablet ? `${BOTTOM_NAV_RESERVED_HEIGHT}px` : {xs: 1, sm: 1, md: 4},
            }}
         >
            <Outlet />
         </Box>
         {device.isPhoneOrTablet && (
            <AppBar position='fixed' color='primary' sx={{top: 'auto', bottom: 0}}>
               <BottomNav />
            </AppBar>
         )}
      </Box>
   );
}
